import React, { useContext } from 'react';
import { Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Tooltip as MuiTooltip, Typography } from '@mui/material';
import { OverallMetricsWithTitle } from '../../../interfaces/Metrics';
import { SettingsContext } from '../../../context/SettingsContext';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import PercentIcon from '@mui/icons-material/Percent';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import BarChartIcon from '@mui/icons-material/BarChart';
import { useTranslation } from '../../../context/TranslationContext';
import { Area, AreaChart, Tooltip, YAxis } from 'recharts';

const getColor = (key: string, value: number | string, darkMode: boolean) => {
  const green = darkMode ? '#089981' : 'green';
  const red = darkMode ? '#FF6347' : 'red';

  if (typeof value === 'number') {
    if (key === 'totalNetProfit') return value >= 1 ? green : red;
    if (key === 'grossProfit') return value >= 1 ? green : red;
    if (key === 'grossLoss') return value >= 1 ? green : red;
    // if (key === 'profitFactor') return value >= 1 ? green : red;
    if (key === 'maxConsLosers') return red;
    if (key === 'maxConsWinners') return green;
    if (key === 'maxDrawdown') return red;
    if (key === 'maxDrawdownDuration') return red;
    if (key === 'maxIntradayDrawdown') return red;
    if (key === 'maxIntradayDrawdownDuration') return red;
    if (key === 'maxRunup') return green;
    if (key === 'maxRunupDuration') return green;
    if (key === 'winningTrades') return value >= 0 ? green : red;
    if (key === 'losingTrades') return value >= 0 ? green : red;
    if (key === 'avgTradeNetProfit') return value >= 0 ? green : red;
    if (key === 'avgWinningTrade') return value >= 0 ? green : red;
    if (key === 'avgLosingTrade') return value >= 0 ? green : red;
    if (key === 'largestWin') return value >= 0 ? green : red;
    if (key === 'largestLoss') return value >= 0 ? green : red;
    if (key === 'profitPerLosingTrade') return value >= 0 ? green : red;
    if (key === 'lossPerWinningTrade') return value >= 0 ? green : red;
    if (key === 'lossPerLosingTrade') return value >= 0 ? green : red;
    if (key === 'annualRateOfReturn') return value >= 0 ? green : red;
    if (key === 'sharpeRatio') return value >= 1 ? green : red;
    if (key === 'sortinoRatio') return value >= 1 ? green : red;
    if (key === 'sterlingRatio') return value >= 1 ? green : red;
    if (key === 'marRatio') return value >= 1 ? green : red;
    if (key === 'efficiencyFactor') return value >= 1 ? green : red;
    if (key === 'returnOnInitialCapital') return value >= 0 ? green : red;
    if (key === 'marginRequirements') return value >= 0 ? green : red;
    if (key === 'maxDrawdownAsPercentOfInitialEquity') return red;
    if (key === 'maxDrawdownAsPercentOfTotalEquity') return red;
    if (key === 'maxIntradayDrawdownAsPercentOfInitialEquity') return red;
    if (key === 'maxIntradayDrawdownAsPercentOfTotalEquity') return red;
    if (key === 'recoveryFactor') return value >= 1 ? green : red;
    if (key === 'npMaxDD') return value >= 1 ? green : red;
    if (key === 'npIntradayMaxDD') return value >= 1 ? green : red;
    if (key === 'avgWorst5DD') return red;
    if (key === 'maxRunupAsPercentOfInitialEquity') return green;
    if (key === 'maxRunupAsPercentOfTotalEquity') return green;
    if (key === 'maxIntradayRunupAsPercentOfInitialEquity') return green;
    if (key === 'maxIntradayRunupAsPercentOfTotalEquity') return green;
    if (key === 'maxIntradayRunupDuration') return green;
    if (key === 'maxIntradayRunup') return green;
    if (key === 'maxIntradayRunupDate') return green;

    return undefined;
  }
  return undefined;
};

const getDataCellStyle = (metric: string, value: number | string, darkMode: boolean) => {
  let result: React.CSSProperties = {
    minWidth: '220px',
    width: "50%",
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    color: getColor(metric, value, darkMode)
  };
  switch (metric) {
    case 'initialCapital':
      result = {
        ...result,
      }
      break;
  }
      
  return result;
};

const iconStyle = { fontSize: '1rem', verticalAlign: 'middle', marginRight: '4px', marginBottom: '3px' };

const TradingMetricsFull: React.FC<{ metricsArray: OverallMetricsWithTitle[], fullMetrics: boolean }> = ({ metricsArray, fullMetrics }) => {
  const { darkMode, getLocale, showCurrencySymbol, getCurrencySymbol } = useContext(SettingsContext);
  
  const formatValue = (key: string, value: number | string, decimals: number) => {
    if (typeof value === 'number') {
      if (key.toLowerCase().includes('trades') || key.toLowerCase().includes('count')) {
        return value.toLocaleString(getLocale(), { minimumFractionDigits: 0, maximumFractionDigits: 0 }); // Use integers for trade counts
      }
      return value.toLocaleString(getLocale(), { minimumFractionDigits: decimals, maximumFractionDigits: decimals }); // Use specified decimals for other numeric values
    }
    return value;
  };

  const formatValueWithCurrency = (key: string, value: number | string, decimals: number) => {
    const formattedValue = formatValue(key, value, decimals);
    if (typeof value === 'number' && showCurrencySymbol) {
      return `${formattedValue} ${getCurrencySymbol()}`;
    }
    return formattedValue;
  };

  const { t } = useTranslation();
  const decimals = 2;
  const firstColumnStyle = {
    width: '200px',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  };
  const lastColumnStyle = {
    width: '100%',
  };

  if (!metricsArray || metricsArray.length === 0) return <div>Error: No metrics available</div>;

  const getGradientOffset = (data: { datetime: Date; totalNetProfit: number; }[], initialCapital: number) => {
    // alert(data[0].totalNetProfit);
    const threshold = initialCapital; // The value where the red area begins
    const dataMax = Math.max(...data.map((i) => i.totalNetProfit));
    const dataMin = Math.min(...data.map((i) => i.totalNetProfit));
    
    // If the data is below the threshold, return 0
    if (dataMax <= threshold) {
        return 0;
    }
    if (dataMin >= threshold) {
        return 1;
    }
    // Otherwise, return the percentage of the distance between the threshold and the max value
    return (dataMax - threshold) / (dataMax - dataMin);
  };
  
  const formatDate = (date: Date) => {
      return date.toLocaleDateString(getLocale());
  };

  const CustomTooltip = ({ active, payload }: any) => {
      if (active && payload && payload.length) {
          return (
              <MuiTooltip title="" placement="top">
                  <Paper style={{ padding: 4, margin: 4, }}>
                      {payload.map((entry: any, index: number) => (
                          entry.value !== 0 && (
                              <p key={`item-${index}`} style={{ color: entry.color }}>
                                  <Typography variant='subtitle2' color='primary'>{t("Date")}: {formatDate(new Date(entry.payload.datetime))}</Typography>
                                  <Typography>{`${formatValue('', entry.value, 2)}`}</Typography>
                              </p>
                          )
                      ))}
                  </Paper>
              </MuiTooltip>
          );
      }
      return null;
  };

  const renderEquityGraph = (metrics: OverallMetricsWithTitle) => {  
    const equityData = metrics.tradingData.map(trade => ({ datetime: new Date(trade.datetime), totalNetProfit: trade.cumulativeProfit + metrics.metrics.initialCapital }));
    // const equityData = metrics.tradingData.map(trade => ({ datetime: new Date(trade.datetime), totalNetProfit: trade.total, initialCapital: metrics.metrics.initialCapital }));

    return (
      <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
        <AreaChart
          data={equityData}
          height={60}
          width={200}
        >
          <defs>
              <linearGradient id={"colorGraph" + metrics.title} x1="0" y1="0" x2="0" y2="1">
                  <stop offset={getGradientOffset(equityData, metrics.metrics.initialCapital || 0) - 0.7} stopColor="#089981" stopOpacity={0.9} />
                  <stop offset={getGradientOffset(equityData, metrics.metrics.initialCapital || 0)} stopColor="#089981" stopOpacity={0.7} />
                  <stop offset={getGradientOffset(equityData, metrics.metrics.initialCapital || 0)} stopColor="#c93939" stopOpacity={0.8} />
                  <stop offset={getGradientOffset(equityData, metrics.metrics.initialCapital || 0) + 0.5} stopColor="#c93939" stopOpacity={1} />
              </linearGradient>
          </defs>
          <YAxis tick={false} axisLine={false} domain={[Math.min(...equityData.map((i) => i.totalNetProfit)), 'dataMin']} />
          <Tooltip content={<CustomTooltip />} cursor={{ fill: 'rgba(255, 255, 255, 0.1)' }} />
          <Area type="monotone" dataKey="totalNetProfit" stroke={`url(#colorGraph${metrics.title})`} fill={`url(#colorGraph${metrics.title})`} strokeWidth={2} baseValue={metrics.metrics.initialCapital}/>
        </AreaChart>
      </div>
    );
  }

  return (
      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell style={firstColumnStyle}><Typography><strong>{t("Metric")}</strong></Typography></TableCell>
              {metricsArray.map(metricsWithTitle => (
                <TableCell align='right' key={metricsWithTitle.title}><Typography><strong>{metricsWithTitle.title}</strong></Typography></TableCell>
              ))}
              <TableCell style={lastColumnStyle}></TableCell>
            </TableRow>
            <TableRow>
              <TableCell style={firstColumnStyle}><Typography></Typography></TableCell>
              {metricsArray.map(metricsWithTitle => (
                <TableCell align='right' key={metricsWithTitle.title}>{renderEquityGraph(metricsWithTitle)}</TableCell>
              ))}
              <TableCell style={lastColumnStyle}></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            <TableRow>
              <TableCell style={firstColumnStyle}><Typography><AttachMoneyIcon style={iconStyle} /><strong>{t("Initial Capital")}</strong></Typography></TableCell>
              {metricsArray.map(metricsWithTitle => (
                <TableCell align='right' key={metricsWithTitle.title} style={getDataCellStyle('initialCapital', metricsWithTitle.metrics.initialCapital, darkMode)}>
                  <Typography>
                    {formatValueWithCurrency('initialCapital', metricsWithTitle.metrics.initialCapital, decimals)}
                  </Typography>
                </TableCell>
              ))}
            </TableRow>
            <TableRow>
              <TableCell style={firstColumnStyle}><Typography><AttachMoneyIcon style={iconStyle} /><strong>{t("Total Net Profit")}</strong></Typography></TableCell>
              {metricsArray.map(metricsWithTitle => (
                <TableCell align='right' key={metricsWithTitle.title} style={getDataCellStyle('totalNetProfit', metricsWithTitle.metrics.totalNetProfit, darkMode)}>
                  <Typography>
                    {formatValueWithCurrency('totalNetProfit', metricsWithTitle.metrics.totalNetProfit, decimals)}
                  </Typography>
                </TableCell>
              ))}
            </TableRow>
            <TableRow>
              <TableCell style={firstColumnStyle}><Typography><AttachMoneyIcon style={iconStyle} /><strong>{t("Gross Profit")}</strong></Typography></TableCell>
              {metricsArray.map(metricsWithTitle => (
                <TableCell align='right' key={metricsWithTitle.title} style={getDataCellStyle('grossProfit', metricsWithTitle.metrics.grossProfit, darkMode)}>
                  <Typography>
                    {formatValueWithCurrency('grossProfit', metricsWithTitle.metrics.grossProfit, decimals)}
                  </Typography>
                </TableCell>
              ))}
            </TableRow>
            <TableRow>
              <TableCell style={firstColumnStyle}><Typography><AttachMoneyIcon style={iconStyle} /><strong>{t("Gross Loss")}</strong></Typography></TableCell>
              {metricsArray.map(metricsWithTitle => (
                <TableCell align='right' key={metricsWithTitle.title} style={getDataCellStyle('grossLoss', metricsWithTitle.metrics.grossLoss, darkMode)}>
                  <Typography>
                    {formatValueWithCurrency('grossLoss', metricsWithTitle.metrics.grossLoss, decimals)}
                  </Typography>
                </TableCell>
              ))}
            </TableRow>
            <TableRow>
              <TableCell style={firstColumnStyle}><Typography><TrendingUpIcon style={iconStyle} /><strong>{t("Profit Factor")}</strong></Typography></TableCell>
              {metricsArray.map(metricsWithTitle => (
                <TableCell align='right' key={metricsWithTitle.title} style={getDataCellStyle('profitFactor', metricsWithTitle.metrics.profitFactor, darkMode)}>
                  <Typography>{formatValue('profitFactor', metricsWithTitle.metrics.profitFactor, decimals)}</Typography>
                </TableCell>
              ))}
            </TableRow>
            { fullMetrics && (
              <TableRow>
                <TableCell style={firstColumnStyle}><Typography><TrendingDownIcon style={iconStyle} /><strong>{t("Pessimistic RR")}</strong></Typography></TableCell>
                {metricsArray.map(metricsWithTitle => (
                  <TableCell align='right' key={metricsWithTitle.title} style={getDataCellStyle('pessimisticRR', metricsWithTitle.metrics.pessimisticRR, darkMode)}>
                    <Typography>{formatValue('pessimisticRR', metricsWithTitle.metrics.pessimisticRR, decimals)}</Typography>
                  </TableCell>
                ))}
              </TableRow>
            )}
            <TableRow>
              <TableCell style={firstColumnStyle}><Typography><BarChartIcon style={iconStyle} /><strong>{t("Total Trades")}</strong></Typography></TableCell>
              {metricsArray.map(metricsWithTitle => (
                <TableCell align='right' key={metricsWithTitle.title} style={getDataCellStyle('totalTrades', metricsWithTitle.metrics.totalTrades, darkMode)}>
                  <Typography>{formatValue('totalTrades', metricsWithTitle.metrics.totalTrades, decimals)}</Typography>
                </TableCell>
              ))}
            </TableRow>
            <TableRow>
              <TableCell style={firstColumnStyle}><Typography><TrendingUpIcon style={iconStyle} /><strong>{t("Winning Trades")}</strong></Typography></TableCell>
              {metricsArray.map(metricsWithTitle => (
                <TableCell align='right' key={metricsWithTitle.title} style={getDataCellStyle('winningTradesCount', metricsWithTitle.metrics.winningTradesCount, darkMode)}>
                  <Typography>{formatValue('winningTradesCount', metricsWithTitle.metrics.winningTradesCount, decimals)}</Typography>
                </TableCell>
              ))}
            </TableRow>
            <TableRow>
              <TableCell style={firstColumnStyle}><Typography><TrendingDownIcon style={iconStyle} /><strong>{t("Losing Trades")}</strong></Typography></TableCell>
              {metricsArray.map(metricsWithTitle => (
                <TableCell align='right' key={metricsWithTitle.title} style={getDataCellStyle('losingTradesCount', metricsWithTitle.metrics.losingTradesCount, darkMode)}>
                  <Typography>{formatValue('losingTradesCount', metricsWithTitle.metrics.losingTradesCount, decimals)}</Typography>
                </TableCell>
              ))}
            </TableRow>
            <TableRow>
              <TableCell style={firstColumnStyle}><Typography><PercentIcon style={iconStyle} /><strong>{t("Percent Profitable Trades")}</strong></Typography></TableCell>
              {metricsArray.map(metricsWithTitle => (
                <TableCell align='right' key={metricsWithTitle.title} style={getDataCellStyle('percentProfitable', metricsWithTitle.metrics.percentProfitable, darkMode)}>
                  <Typography>{formatValue('percentProfitable', metricsWithTitle.metrics.percentProfitable, decimals)} %</Typography>
                </TableCell>
              ))}
            </TableRow>
            <TableRow>
              <TableCell style={firstColumnStyle}><Typography><AttachMoneyIcon style={iconStyle} /><strong>{t("Avg Trade Net Profit")}</strong></Typography></TableCell>
              {metricsArray.map(metricsWithTitle => (
                <TableCell align='right' key={metricsWithTitle.title} style={getDataCellStyle('avgTradeNetProfit', metricsWithTitle.metrics.avgTradeNetProfit, darkMode)}>
                  <Typography>{formatValueWithCurrency('avgTradeNetProfit', metricsWithTitle.metrics.avgTradeNetProfit, decimals)}</Typography>
                </TableCell>
              ))}
            </TableRow>
            <TableRow>
              <TableCell style={firstColumnStyle}><Typography><AttachMoneyIcon style={iconStyle} /><strong>{t("Avg Winning Trade")}</strong></Typography></TableCell>
              {metricsArray.map(metricsWithTitle => (
                <TableCell align='right' key={metricsWithTitle.title} style={getDataCellStyle('avgWinningTrade', metricsWithTitle.metrics.avgWinningTrade, darkMode)}>
                  <Typography>{formatValueWithCurrency('avgWinningTrade', metricsWithTitle.metrics.avgWinningTrade, decimals)}</Typography>
                </TableCell>
              ))}
            </TableRow>
            <TableRow>
              <TableCell style={firstColumnStyle}><Typography><AttachMoneyIcon style={iconStyle} /><strong>{t("Avg Losing Trade")}</strong></Typography></TableCell>
              {metricsArray.map(metricsWithTitle => (
                <TableCell align='right' key={metricsWithTitle.title} style={getDataCellStyle('avgLosingTrade', metricsWithTitle.metrics.avgLosingTrade, darkMode)}>
                  <Typography>{formatValueWithCurrency('avgLosingTrade', metricsWithTitle.metrics.avgLosingTrade, decimals)}</Typography>
                </TableCell>
              ))}
            </TableRow>
            { fullMetrics && (
              <>
                <TableRow>
                  <TableCell style={firstColumnStyle}></TableCell>
                </TableRow>
                <TableRow>
                  <TableCell style={firstColumnStyle}><Typography><AttachMoneyIcon style={iconStyle} /><strong>{t("Largest Win")}</strong></Typography></TableCell>
                  {metricsArray.map(metricsWithTitle => (
                    <TableCell align='right' key={metricsWithTitle.title} style={getDataCellStyle('largestWin', metricsWithTitle.metrics.largestWin, darkMode)}>
                      <Typography>{formatValueWithCurrency('largestWin', metricsWithTitle.metrics.largestWin, decimals)}</Typography>
                    </TableCell>
                  ))}
                </TableRow>
                <TableRow>
                  <TableCell style={firstColumnStyle}><Typography><TrendingUpIcon style={iconStyle} /><strong>{t("Max Cons Winners")}</strong></Typography></TableCell>
                  {metricsArray.map(metricsWithTitle => (
                    <TableCell align='right' key={metricsWithTitle.title} style={getDataCellStyle('maxConsWinners', metricsWithTitle.metrics.maxConsWinners, darkMode)}>
                      <Typography>{formatValue('maxConsWinners', metricsWithTitle.metrics.maxConsWinners, 0)}</Typography>
                    </TableCell>
                  ))}
                </TableRow>
                <TableRow>
                  <TableCell style={firstColumnStyle}><Typography><AttachMoneyIcon style={iconStyle} /><strong>{t("Largest Loss")}</strong></Typography></TableCell>
                  {metricsArray.map(metricsWithTitle => (
                    <TableCell align='right' key={metricsWithTitle.title} style={getDataCellStyle('largestLoss', metricsWithTitle.metrics.largestLoss, darkMode)}>
                      <Typography>{formatValueWithCurrency('largestLoss', metricsWithTitle.metrics.largestLoss, decimals)}</Typography>
                    </TableCell>
                  ))}
                </TableRow>
                <TableRow>
                  <TableCell style={firstColumnStyle}><Typography><TrendingDownIcon style={iconStyle} /><strong>{t("Max Cons Losers")}</strong></Typography></TableCell>
                  {metricsArray.map(metricsWithTitle => (
                    <TableCell align='right' key={metricsWithTitle.title} style={getDataCellStyle('maxConsLosers', metricsWithTitle.metrics.maxConsLosers, darkMode)}>
                      <Typography>{formatValue('maxConsLosers', metricsWithTitle.metrics.maxConsLosers, 0)}</Typography>
                    </TableCell>
                  ))}
                </TableRow>
                <TableRow>
                  <TableCell style={firstColumnStyle}></TableCell>
                </TableRow>
                <TableRow>
                  <TableCell style={firstColumnStyle}><Typography><CalendarTodayIcon style={iconStyle} /><strong>{t("Trading Period")}</strong></Typography></TableCell>
                  {metricsArray.map(metricsWithTitle => (
                    <TableCell align='right' key={metricsWithTitle.title}>
                      <Typography>{new Date(metricsWithTitle.metrics.tradingPeriod.start).toLocaleDateString() + ' - ' + new Date(metricsWithTitle.metrics.tradingPeriod.end).toLocaleDateString()}</Typography>
                      <Typography>{ '(' + Math.abs((metricsWithTitle.metrics.tradingPeriod.end - metricsWithTitle.metrics.tradingPeriod.start) / 1000 / 60 / 60 / 24).toFixed(0)  + ` ${t("days")})`}</Typography>
                    </TableCell>
                  ))}
                </TableRow>
                <TableRow>
                  <TableCell style={firstColumnStyle}><Typography><CalendarTodayIcon style={iconStyle} /><strong>{t("Total Trading Days")}</strong></Typography></TableCell>
                  {metricsArray.map(metricsWithTitle => (
                    <TableCell align='right' key={metricsWithTitle.title}>
                      <Typography>{metricsWithTitle.metrics.tradingPeriod.tradingDays} {t("days")}</Typography>
                    </TableCell>
                  ))}
                </TableRow>
                <TableRow>
                  <TableCell style={firstColumnStyle}><Typography><CalendarTodayIcon style={iconStyle} /><strong>{t("Longest Flat Period Days")}</strong></Typography></TableCell>
                  {metricsArray.map(metricsWithTitle => (
                    <TableCell align='right' key={metricsWithTitle.title}>
                      <Typography>{formatValue('longestFlatPeriodDays', metricsWithTitle.metrics.longestFlatPeriodDays, 0)} {t("days")}</Typography>
                    </TableCell>
                  ))}
                </TableRow>
                <TableRow>
                  <TableCell style={firstColumnStyle}></TableCell>
                </TableRow>
                <TableRow>
                  <TableCell style={firstColumnStyle}><Typography><TrendingDownIcon style={iconStyle} /><strong>{t("Max Drawdown")}</strong></Typography></TableCell>
                  {metricsArray.map(metricsWithTitle => (
                    <TableCell align='right' key={metricsWithTitle.title} style={getDataCellStyle('maxDrawdown', metricsWithTitle.metrics.maxDrawdown.maxDrawdown, darkMode)}>
                      <Typography>{formatValueWithCurrency('maxDrawdown', metricsWithTitle.metrics.maxDrawdown.maxDrawdown, decimals)}</Typography>
                    </TableCell>
                  ))}
                </TableRow>
                <TableRow>
                  <TableCell style={firstColumnStyle}><Typography><PercentIcon style={iconStyle} /><strong>{t("Max Drawdown As % Of Initial Equity")}</strong></Typography></TableCell>
                  {metricsArray.map(metricsWithTitle => (
                    <TableCell align='right' key={metricsWithTitle.title} style={getDataCellStyle('maxDrawdown', metricsWithTitle.metrics.maxDrawdown.asPercentOfInitialEquity, darkMode)}>
                      <Typography>{formatValue('maxDrawdown', metricsWithTitle.metrics.maxDrawdown.asPercentOfInitialEquity, decimals)} %</Typography>
                    </TableCell>
                  ))}
                </TableRow>
                <TableRow>
                  <TableCell style={firstColumnStyle}><Typography><PercentIcon style={iconStyle} /><strong>{t("Max Drawdown As % Of Total Equity")}</strong></Typography></TableCell>
                  {metricsArray.map(metricsWithTitle => (
                    <TableCell align='right' key={metricsWithTitle.title} style={getDataCellStyle('maxDrawdown', metricsWithTitle.metrics.maxDrawdown.asPercentOfTotalEquity, darkMode)}>
                      <Typography>{formatValue('maxDrawdown',  metricsWithTitle.metrics.maxDrawdown.asPercentOfTotalEquity, decimals)} %</Typography>
                    </TableCell>
                  ))}
                </TableRow>
                <TableRow>
                  <TableCell style={firstColumnStyle}><Typography><CalendarTodayIcon style={iconStyle} /><strong>{t("Max Drawdown Date")}</strong></Typography></TableCell>
                  {metricsArray.map(metricsWithTitle => (
                    <TableCell align='right' key={metricsWithTitle.title}>
                      <Typography>{formatValue('maxDrawdownDate', new Date(metricsWithTitle.metrics.maxDrawdown.maxDrawdownDate).toLocaleDateString(), decimals)}</Typography>
                    </TableCell>
                  ))}
                </TableRow>
                <TableRow>
                  <TableCell style={firstColumnStyle}><Typography><TrendingDownIcon style={iconStyle} /><strong>{t("Max Intraday Drawdown")}</strong></Typography></TableCell>
                  {metricsArray.map(metricsWithTitle => (
                    <TableCell align='right' key={metricsWithTitle.title} style={getDataCellStyle('maxIntradayDrawdown', metricsWithTitle.metrics.maxIntradayDrawdown.maxDrawdown, darkMode)}>
                      <Typography>{formatValueWithCurrency('maxIntradayDrawdown', metricsWithTitle.metrics.maxIntradayDrawdown.maxDrawdown, decimals)}</Typography>
                    </TableCell>
                  ))}
                </TableRow>

                <TableRow>
                  <TableCell style={firstColumnStyle}><Typography><PercentIcon style={iconStyle} /><strong>{t("Max Intraday Drawdown As % Of Initial Equity")}</strong></Typography></TableCell>
                  {metricsArray.map(metricsWithTitle => (
                    <TableCell align='right' key={metricsWithTitle.title} style={getDataCellStyle('maxIntradayDrawdown', metricsWithTitle.metrics.maxIntradayDrawdown.asPercentOfInitialEquity, darkMode)}>
                      <Typography>{formatValue('maxIntradayDrawdown', metricsWithTitle.metrics.maxIntradayDrawdown.asPercentOfInitialEquity, decimals)} %</Typography>
                    </TableCell>
                  ))}
                </TableRow>
                <TableRow>
                  <TableCell style={firstColumnStyle}><Typography><PercentIcon style={iconStyle} /><strong>{t("Max Intraday Drawdown As % Of Total Equity")}</strong></Typography></TableCell>
                  {metricsArray.map(metricsWithTitle => (
                    <TableCell align='right' key={metricsWithTitle.title} style={getDataCellStyle('maxIntradayDrawdown', metricsWithTitle.metrics.maxIntradayDrawdown.asPercentOfTotalEquity, darkMode)}>
                      <Typography>{formatValue('maxIntradayDrawdown', metricsWithTitle.metrics.maxIntradayDrawdown.asPercentOfTotalEquity, decimals)} %</Typography>
                    </TableCell>
                  ))}
                </TableRow>
                <TableRow>
                  <TableCell style={firstColumnStyle}><Typography><CalendarTodayIcon style={iconStyle} /><strong>{t("Max Intraday Drawdown Date")}</strong></Typography></TableCell>
                  {metricsArray.map(metricsWithTitle => (
                    <TableCell align='right' key={metricsWithTitle.title}>
                      <Typography>{new Date(metricsWithTitle.metrics.maxIntradayDrawdown.maxDrawdownDate).toLocaleDateString()}</Typography>
                    </TableCell>
                  ))}
                </TableRow>
                <TableRow>
                  <TableCell style={firstColumnStyle}><Typography><CalendarTodayIcon style={iconStyle} /><strong>{t("Longest Drawdown Days")}</strong></Typography></TableCell>
                  {metricsArray.map(metricsWithTitle => (
                    <TableCell align='right' key={metricsWithTitle.title} style={getDataCellStyle('longestDrawdownDays', metricsWithTitle.metrics.longestDrawdownDays, darkMode)}>
                      <Typography>{formatValue('longestDrawdownDays', metricsWithTitle.metrics.longestDrawdownDays, 0)}</Typography>
                    </TableCell>
                  ))}
                </TableRow>
                <TableRow>
                  <TableCell style={firstColumnStyle}><Typography><TrendingUpIcon style={iconStyle} /><strong>{t("Recovery Factor")}</strong></Typography></TableCell>
                  {metricsArray.map(metricsWithTitle => (
                    <TableCell align='right' key={metricsWithTitle.title} style={getDataCellStyle('recoveryFactor', metricsWithTitle.metrics.recoveryFactor, darkMode)}>
                      <Typography>{formatValue('recoveryFactor', metricsWithTitle.metrics.recoveryFactor, decimals)}</Typography>
                    </TableCell>
                  ))}
                </TableRow>
                <TableRow>
                  <TableCell style={firstColumnStyle}><Typography><AttachMoneyIcon style={iconStyle} /><strong>{t("NP Max DD")}</strong></Typography></TableCell>
                  {metricsArray.map(metricsWithTitle => (
                    <TableCell align='right' key={metricsWithTitle.title} style={getDataCellStyle('npMaxDD', metricsWithTitle.metrics.npMaxDD, darkMode)}>
                      <Typography>{formatValueWithCurrency('npMaxDD', metricsWithTitle.metrics.npMaxDD, decimals)}</Typography>
                    </TableCell>
                  ))}
                </TableRow>
                <TableRow>
                  <TableCell style={firstColumnStyle}><Typography><AttachMoneyIcon style={iconStyle} /><strong>{t("NP Intraday Max DD")}</strong></Typography></TableCell>
                  {metricsArray.map(metricsWithTitle => (
                    <TableCell align='right' key={metricsWithTitle.title} style={getDataCellStyle('npIntradayMaxDD', metricsWithTitle.metrics.npIntradayMaxDD, darkMode)}>
                      <Typography>{formatValueWithCurrency('npIntradayMaxDD', metricsWithTitle.metrics.npIntradayMaxDD, decimals)}</Typography>
                    </TableCell>
                  ))}
                </TableRow>
                <TableRow>
                  <TableCell style={firstColumnStyle}><Typography><AttachMoneyIcon style={iconStyle} /><strong>{t("Avg Worst 5 DD")}</strong></Typography></TableCell>
                  {metricsArray.map(metricsWithTitle => (
                    <TableCell align='right' key={metricsWithTitle.title} style={getDataCellStyle('avgWorst5DD', metricsWithTitle.metrics.avgWorst5DD.avgWorst5DD, darkMode)}>
                      <Typography>{formatValueWithCurrency('avgWorst5DD', metricsWithTitle.metrics.avgWorst5DD.avgWorst5DD, decimals)}</Typography>
                    </TableCell>
                  ))}
                </TableRow>
                <TableRow>
                  <TableCell style={firstColumnStyle}><Typography><PercentIcon style={iconStyle} /><strong>{t("Avg Worst 5 DD As % Of Initial Equity")}</strong></Typography></TableCell>

                  {metricsArray.map(metricsWithTitle => (
                    <TableCell align='right' key={metricsWithTitle.title} style={getDataCellStyle('avgWorst5DD', metricsWithTitle.metrics.avgWorst5DD.asPercentOfInitialEquity, darkMode)}>
                      <Typography>{formatValue('avgWorst5DD', metricsWithTitle.metrics.avgWorst5DD.asPercentOfInitialEquity, decimals)} %</Typography>
                    </TableCell>
                  ))}
                </TableRow>
                <TableRow>
                  <TableCell style={firstColumnStyle}></TableCell>
                </TableRow>                  
                <TableRow>
                  <TableCell style={firstColumnStyle}><Typography><TrendingUpIcon style={iconStyle} /><strong>{t("Max Runup")}</strong></Typography></TableCell>
                  {metricsArray.map(metricsWithTitle => (
                    <TableCell align='right' key={metricsWithTitle.title} style={getDataCellStyle('maxRunup', metricsWithTitle.metrics.maxRunup.maxRunup, darkMode)}>
                      <Typography>{formatValueWithCurrency('maxRunup', metricsWithTitle.metrics.maxRunup.maxRunup, decimals)}</Typography>
                    </TableCell>
                  ))}
                </TableRow>
                <TableRow>
                  <TableCell style={firstColumnStyle}><Typography><PercentIcon style={iconStyle} /><strong>{t("Max Runup As % Of Initial Equity")}</strong></Typography></TableCell>
                  {metricsArray.map(metricsWithTitle => (
                    <TableCell align='right' key={metricsWithTitle.title} style={getDataCellStyle('maxRunup', metricsWithTitle.metrics.maxRunup.asPercentOfInitialEquity, darkMode)}>
                      <Typography>{formatValue('maxRunup',  metricsWithTitle.metrics.maxRunup.asPercentOfInitialEquity, decimals)} %</Typography>
                    </TableCell>
                  ))}
                </TableRow>
                <TableRow>
                  <TableCell style={firstColumnStyle}><Typography><PercentIcon style={iconStyle} /><strong>{t("Max Runup As % Of Total Equity")}</strong></Typography></TableCell>
                  {metricsArray.map(metricsWithTitle => (
                    <TableCell align='right' key={metricsWithTitle.title} style={getDataCellStyle('maxRunup', metricsWithTitle.metrics.maxRunup.asPercentOfTotalEquity, darkMode)}>
                      <Typography>{formatValue('maxRunup',  metricsWithTitle.metrics.maxRunup.asPercentOfTotalEquity, decimals)} %</Typography>
                    </TableCell>
                  ))}
                </TableRow>
                <TableRow>
                  <TableCell style={firstColumnStyle}><Typography><CalendarTodayIcon style={iconStyle} /><strong>{t("Max Runup Date")}</strong></Typography></TableCell>
                  {metricsArray.map(metricsWithTitle => (
                    <TableCell align='right' key={metricsWithTitle.title} style={getDataCellStyle('maxRunupDate', metricsWithTitle.metrics.maxRunup.maxRunupDate, darkMode)}>
                      <Typography>{formatValue('maxRunupDate', new Date(metricsWithTitle.metrics.maxRunup.maxRunupDate).toLocaleDateString(), 0)}</Typography>
                    </TableCell>
                  ))}
                </TableRow>
                <TableRow>
                  <TableCell style={firstColumnStyle}><Typography><CalendarTodayIcon style={iconStyle} /><strong>{t("Longest Runup Days")}</strong></Typography></TableCell>
                  {metricsArray.map(metricsWithTitle => (
                    <TableCell align='right' key={metricsWithTitle.title} style={getDataCellStyle('longestRunupDays', metricsWithTitle.metrics.longestRunupDays, darkMode)}>
                      <Typography>{formatValue('longestRunupDays', metricsWithTitle.metrics.longestRunupDays, 0)}</Typography>
                    </TableCell>
                  ))}
                </TableRow>
                <TableRow>
                  <TableCell style={firstColumnStyle}></TableCell>
                </TableRow>
                <TableRow>
                  <TableCell style={firstColumnStyle}><Typography><AttachMoneyIcon style={iconStyle} /><strong>{t("Initial Capital")}</strong></Typography></TableCell>
                  {metricsArray.map(metricsWithTitle => (
                    <TableCell align='right' key={metricsWithTitle.title} style={getDataCellStyle('initialCapital', metricsWithTitle.metrics.initialCapital, darkMode)}>
                      <Typography>{formatValueWithCurrency('initialCapital', metricsWithTitle.metrics.initialCapital, decimals)}</Typography>
                    </TableCell>
                  ))}
                </TableRow>
                <TableRow>
                  <TableCell style={firstColumnStyle}><Typography><AttachMoneyIcon style={iconStyle} /><strong>{t("Margin Requirements")}</strong></Typography></TableCell>
                  {metricsArray.map(metricsWithTitle => (
                    <TableCell align='right' key={metricsWithTitle.title} style={getDataCellStyle('marginRequirements', metricsWithTitle.metrics.marginRequirements, darkMode)}>
                      <Typography>{formatValueWithCurrency('marginRequirements', metricsWithTitle.metrics.marginRequirements, decimals)}</Typography>
                    </TableCell>
                  ))}
                </TableRow>
                <TableRow>
                  <TableCell style={firstColumnStyle}><Typography><AttachMoneyIcon style={iconStyle} /><strong>{t("Return on Initial Capital")}</strong></Typography></TableCell>
                  {metricsArray.map(metricsWithTitle => (
                    <TableCell align='right' key={metricsWithTitle.title} style={getDataCellStyle('returnOnInitialCapital', metricsWithTitle.metrics.returnOnInitialCapital, darkMode)}>
                      <Typography>{formatValue('returnOnInitialCapital', metricsWithTitle.metrics.returnOnInitialCapital, decimals)} %</Typography>
                    </TableCell>
                  ))}
                </TableRow>
                <TableRow>
                  <TableCell style={firstColumnStyle}><Typography><TrendingUpIcon style={iconStyle} /><strong>{t("Annual Rate of Return")}</strong></Typography></TableCell>
                  {metricsArray.map(metricsWithTitle => (
                    <TableCell align='right' key={metricsWithTitle.title} style={getDataCellStyle('annualRateOfReturn', metricsWithTitle.metrics.annualRateOfReturn, darkMode)}>
                      <Typography>{formatValue('annualRateOfReturn', metricsWithTitle.metrics.annualRateOfReturn, decimals)} %</Typography>
                    </TableCell>
                  ))}
                </TableRow>
                <TableRow>
                  <TableCell style={firstColumnStyle}><Typography><TrendingUpIcon style={iconStyle} /><strong>{t("Avg Monthly Return")}</strong></Typography></TableCell>
                  {metricsArray.map(metricsWithTitle => (
                    <TableCell align='right' key={metricsWithTitle.title} style={getDataCellStyle('avgMonthlyReturn', metricsWithTitle.metrics.avgMonthlyReturn, darkMode)}>
                      <Typography>{formatValue('avgMonthlyReturn', metricsWithTitle.metrics.avgMonthlyReturn, decimals)}</Typography>
                    </TableCell>
                  ))}
                </TableRow>
                <TableRow>
                  <TableCell style={firstColumnStyle}><Typography><TrendingUpIcon style={iconStyle} /><strong>{t("Std Dev Monthly Return")}</strong></Typography></TableCell>
                  {metricsArray.map(metricsWithTitle => (
                    <TableCell align='right' key={metricsWithTitle.title} style={getDataCellStyle('stdDevMonthlyReturn', metricsWithTitle.metrics.stdDevMonthlyReturn, darkMode)}>
                      <Typography>{formatValue('stdDevMonthlyReturn', metricsWithTitle.metrics.stdDevMonthlyReturn, decimals)}</Typography>
                    </TableCell>
                  ))}
                </TableRow>
                <TableRow>
                  <TableCell style={firstColumnStyle}><Typography><PercentIcon style={iconStyle} /><strong>{t("Percent Profitable Months")}</strong></Typography></TableCell>
                  {metricsArray.map(metricsWithTitle => (
                    <TableCell align='right' key={metricsWithTitle.title} style={getDataCellStyle('percentProfitableMonths', metricsWithTitle.metrics.percentProfitableMonths, darkMode)}>
                      <Typography>{formatValue('percentProfitableMonths', metricsWithTitle.metrics.percentProfitableMonths, decimals)}</Typography>
                    </TableCell>
                  ))}
                </TableRow>
                <TableRow>
                  <TableCell style={firstColumnStyle}></TableCell>
                </TableRow>
              </>
            )}
            <TableRow>
              <TableCell style={firstColumnStyle}><Typography><TrendingUpIcon style={iconStyle} /><strong>{t("Sharpe Ratio")}</strong></Typography></TableCell>
              {metricsArray.map(metricsWithTitle => (
                <TableCell align='right' key={metricsWithTitle.title} style={getDataCellStyle('sharpeRatio', metricsWithTitle.metrics.sharpeRatio, darkMode)}>
                  <Typography>{formatValue('sharpeRatio', metricsWithTitle.metrics.sharpeRatio, decimals)}</Typography>
                </TableCell>
              ))}
            </TableRow>      
          </TableBody>
        </Table>
      </TableContainer>
  );
};

export default TradingMetricsFull;
